// TipTap Extension that cleans up Microsoft Word / Google Docs paste HTML and
// converts pseudo-bullet/numbered paragraphs into real <ul>/<ol> structures.
//
// Word/Google Docs frequently paste lists as a flat sequence of paragraphs:
//   <p class="MsoListParagraph">• Item one</p>
//   <p class="MsoListParagraph">1.	Item two</p>
// which TipTap keeps as plain paragraphs. This extension intercepts the
// pasted HTML, groups consecutive list-like paragraphs, and emits proper
// list markup that TipTap can parse into ListItem nodes.
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Fragment, Slice, type Node as PMNode } from "@tiptap/pm/model";
import { serializeBodyFromElement } from "@/lib/briefDocument/footnoteBody";

const BULLET_CHARS = /^[\s\u00a0]*([•·◦▪–—\-*o])[\s\u00a0\t]+/;
const NUM_PREFIX = /^[\s\u00a0]*(\d+)[.)][\s\u00a0\t]+/;
const ML_LEVEL_RE = /mso-list:\s*l\d+\s+level(\d+)/i;

const newFootnoteId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `fn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const escapeAttr = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * Detect Word / Google Docs / LibreOffice footnote anchor pairs in a parsed
 * clipboard Document and replace each in-text anchor with a placeholder
 * `<span class="footnote-ref" data-id data-body>` that TipTap's
 * FootnoteRefNode parses. Removes the matched trailing body elements. Orphan
 * markers (no matching body) and orphan bodies are left untouched.
 *
 * Supported conventions, in priority order:
 *   1. MS Word `_ftn{N}` / `_ftnref{N}` — body `<div id="ftn{N}">…</div>`
 *   2. Google Docs `ftnt{N}` / `ftnt_ref{N}` — body `<div id="ftnt{N}">…</div>`
 *   3. LibreOffice `sdfootnote{N}` — body `<div id="sdfootnote{N}">…</div>`
 *
 * Body marks (<strong> / <em> / <a>) survive through
 * `serializeBodyFromElement`; everything else is flattened to text.
 */
const extractWordFootnotes = (doc: Document): void => {
  type Convention = { bodyIdFor: (key: string) => string; refHrefRe: RegExp };
  const conventions: Convention[] = [
    { bodyIdFor: (n) => `ftn${n}`, refHrefRe: /^#_ftn(\d+)$/ },
    { bodyIdFor: (n) => `ftnt${n}`, refHrefRe: /^#ftnt(\d+)$/ },
    { bodyIdFor: (n) => `sdfootnote${n}`, refHrefRe: /^#sdfootnote(\d+)(?:sym|anc)?$/ },
  ];

  for (const conv of conventions) {
    const anchors = Array.from(doc.querySelectorAll("a[href]")) as HTMLAnchorElement[];
    for (const a of anchors) {
      const href = a.getAttribute("href") ?? "";
      const m = conv.refHrefRe.exec(href);
      if (!m) continue;
      const num = m[1];
      const bodyEl = doc.getElementById(conv.bodyIdFor(num));
      if (!bodyEl) continue;

      // Strip the leading back-link inside the body (e.g. `<a href="#_ftnref1">[1]</a>`).
      const backLinks = bodyEl.querySelectorAll('a[href^="#"]');
      backLinks.forEach((bl) => {
        // Only strip back-links pointing into the ref convention namespace.
        const bh = bl.getAttribute("href") ?? "";
        if (/^#(_ftnref|ftnt_ref|sdfootnote)/.test(bh)) bl.remove();
      });

      const body = serializeBodyFromElement(bodyEl);
      if (!body) {
        // Empty body after stripping — skip this pair, leave anchor alone.
        continue;
      }

      const id = newFootnoteId();
      const placeholder = doc.createElement("span");
      placeholder.setAttribute("class", "footnote-ref");
      placeholder.setAttribute("data-id", id);
      placeholder.setAttribute("data-body", body);

      // Replace the anchor (and a wrapping <sup> if it only contained the anchor).
      const parent = a.parentElement;
      if (parent && parent.tagName.toLowerCase() === "sup" && parent.childNodes.length === 1) {
        parent.replaceWith(placeholder);
      } else {
        a.replaceWith(placeholder);
      }

      // Remove the body element from the DOM.
      bodyEl.remove();
    }
  }
};

// Re-export for tests + sanitizer wiring.
export { extractWordFootnotes, newFootnoteId, escapeAttr };

type ListKind = "ul" | "ol";

type Parsed = { kind: ListKind; level: number; html: string };

const parseListyParagraph = (p: HTMLParagraphElement): Parsed | null => {
  // Detect Word's mso-list marker first (level info lives in style).
  const style = p.getAttribute("style") ?? "";
  const cls = p.getAttribute("class") ?? "";
  const isMsoList = /MsoListParagraph/i.test(cls) || /mso-list:/i.test(style);
  const levelMatch = ML_LEVEL_RE.exec(style);
  const msoLevel = levelMatch ? Math.max(1, Math.min(6, Number(levelMatch[1]))) : null;

  // Strip the leading bullet/number span Word emits.
  let text = p.textContent ?? "";
  let html = p.innerHTML;

  const bulletM = BULLET_CHARS.exec(text);
  const numM = NUM_PREFIX.exec(text);

  if (!isMsoList && !bulletM && !numM) return null;

  // Remove leading list-marker glyph from the html as well by re-rendering
  // text-stripped content: easiest = rebuild from text without the prefix.
  if (bulletM) {
    text = text.replace(BULLET_CHARS, "");
    html = escapeHtml(text);
  } else if (numM) {
    text = text.replace(NUM_PREFIX, "");
    html = escapeHtml(text);
  } else {
    // mso-list without an obvious leading glyph: keep innerHTML but strip
    // any Word marker <span style="mso-list:Ignore">…</span>.
    html = html.replace(/<span[^>]*mso-list:Ignore[^>]*>[\s\S]*?<\/span>/gi, "").trim();
  }

  const kind: ListKind = numM ? "ol" : "ul";
  const level = msoLevel ?? inferLevelFromIndent(style);
  return { kind, level, html };
};

const inferLevelFromIndent = (style: string): number => {
  const m = /margin-left:\s*([\d.]+)(pt|px|in)/i.exec(style);
  if (!m) return 1;
  const v = Number(m[1]);
  const unit = m[2].toLowerCase();
  const px = unit === "pt" ? v * 1.333 : unit === "in" ? v * 96 : v;
  return Math.max(1, Math.min(6, Math.round(px / 36) || 1));
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const renderGroup = (group: Parsed[]): string => {
  // Build nested lists honoring level.
  // Strategy: a simple stack of open lists.
  let out = "";
  const stack: { kind: ListKind; level: number }[] = [];
  for (const item of group) {
    while (stack.length > 0 && stack[stack.length - 1].level > item.level) {
      const closed = stack.pop()!;
      out += `</li></${closed.kind}>`;
    }
    if (
      stack.length === 0 ||
      stack[stack.length - 1].level < item.level ||
      stack[stack.length - 1].kind !== item.kind
    ) {
      // Need to open (or replace top) a list at this level.
      if (
        stack.length > 0 &&
        stack[stack.length - 1].level === item.level &&
        stack[stack.length - 1].kind !== item.kind
      ) {
        const closed = stack.pop()!;
        out += `</li></${closed.kind}>`;
      }
      out += `<${item.kind}><li>${item.html}`;
      stack.push({ kind: item.kind, level: item.level });
    } else {
      out += `</li><li>${item.html}`;
    }
  }
  while (stack.length > 0) {
    const closed = stack.pop()!;
    out += `</li></${closed.kind}>`;
  }
  return out;
};

// Split a block element that contains <br> line breaks into multiple sibling
// blocks of the same tag. Single <br> between content becomes a paragraph
// boundary; trailing <br>s are dropped. Operates on a parsed Document in place.
const splitBlockOnBr = (block: HTMLElement): HTMLElement[] => {
  const tag = block.tagName.toLowerCase();
  // Only split paragraph-like containers. Leave headings/lists/quotes alone —
  // <br> inside those is usually intentional and splitting would break schema.
  if (tag !== "p" && tag !== "div") return [block];

  // Fast path: no <br>, nothing to do.
  if (!block.querySelector("br")) return [block];

  const doc = block.ownerDocument!;
  const groups: Node[][] = [[]];
  for (const child of Array.from(block.childNodes)) {
    if (child.nodeType === 1 && (child as Element).tagName.toLowerCase() === "br") {
      groups.push([]);
    } else {
      groups[groups.length - 1].push(child);
    }
  }

  // Drop trailing empty groups (from trailing <br>s).
  while (groups.length > 1 && groups[groups.length - 1].every(isWhitespaceNode)) {
    groups.pop();
  }
  if (groups.length <= 1) return [block];

  const out: HTMLElement[] = [];
  for (const nodes of groups) {
    if (nodes.length === 0 || nodes.every(isWhitespaceNode)) {
      // Preserve an empty paragraph as a visual blank line.
      const empty = doc.createElement(tag === "div" ? "p" : tag);
      out.push(empty);
      continue;
    }
    const wrapper = doc.createElement(tag === "div" ? "p" : tag);
    // Copy preserved attrs (we already stripped class/style/lang upstream).
    for (const attr of Array.from(block.attributes)) {
      wrapper.setAttribute(attr.name, attr.value);
    }
    for (const n of nodes) wrapper.appendChild(n.cloneNode(true));
    out.push(wrapper);
  }
  return out;
};

const isWhitespaceNode = (n: Node): boolean => {
  if (n.nodeType === 3) return !(n.textContent ?? "").trim();
  if (n.nodeType === 1) {
    const t = (n as Element).tagName.toLowerCase();
    if (t === "br") return true;
  }
  return false;
};

// Walk the body and replace each splittable block with its split siblings.
const splitAllBrParagraphs = (body: HTMLElement) => {
  const blocks = Array.from(body.querySelectorAll("p, div")) as HTMLElement[];
  for (const block of blocks) {
    // Skip blocks nested inside lists/quotes/tables — splitting those would
    // produce invalid markup.
    if (block.closest("li, blockquote, table, figure")) continue;
    const replacements = splitBlockOnBr(block);
    if (replacements.length === 1 && replacements[0] === block) continue;
    const parent = block.parentNode;
    if (!parent) continue;
    for (const r of replacements) parent.insertBefore(r, block);
    parent.removeChild(block);
  }
};

const transform = (html: string): string => {
  // We always run at minimum the <br>-splitting pass so soft-line-break
  // paragraphs (very common in pasted content from email, Word, web) become
  // real paragraph boundaries. Heading toggles + indent + lists all rely on
  // each visible "paragraph" being a distinct <p> node.
  const looksWordy = /mso-|MsoListParagraph|<o:p>/i.test(html);
  const looksBullety = /<p[^>]*>\s*(?:&nbsp;|\s)*[•·◦▪\-*o]/i.test(html);
  const looksNumbery = /<p[^>]*>\s*(?:&nbsp;|\s)*\d+[.)]\s*\t?/i.test(html);
  const hasBr = /<br\b/i.test(html);
  const hasFootnotes = /(?:href="#_ftn\d|href="#ftnt\d|href="#sdfootnote\d)/i.test(html);
  if (!looksWordy && !looksBullety && !looksNumbery && !hasBr && !hasFootnotes) return html;

  const doc = new DOMParser().parseFromString(html, "text/html");

  // Strip Word noise tags/attrs everywhere.
  // NB: Word/Office HTML contains namespaced tags like <o:p> and <w:wordDocument>.
  // CSS selectors with escaped colons (`o\:p`) parse fine in jsdom but throw in
  // happy-dom, so we walk all elements and match on tagName instead.
  Array.from(doc.querySelectorAll("*")).forEach((n) => {
    const tag = n.tagName.toLowerCase();
    if (tag === "o:p" || tag === "w:worddocument" || tag === "xml" || tag === "style") {
      n.remove();
    }
  });
  doc.querySelectorAll("[class],[style],[lang]").forEach((el) => {
    if (el.tagName.toLowerCase() === "p") {
      // We need class/style on <p> for list detection — handled below per-paragraph.
      return;
    }
    // Footnote anchor placeholders carry data-id/data-body but no class/style.
    if (el.tagName.toLowerCase() === "a" && /^#(_ftn|ftnt|sdfootnote)/i.test(el.getAttribute("href") ?? "")) {
      return;
    }
    el.removeAttribute("class");
    el.removeAttribute("style");
    el.removeAttribute("lang");
  });

  // Detect Word/Google Docs/LibreOffice footnote anchor pairs and rewrite them
  // into excerptus.footnoteRef placeholders BEFORE list grouping / <br>
  // splitting touches the DOM. This must run after class/style stripping so the
  // serializer sees clean inline children, but before list grouping so the
  // trailing footnote body <div>s get removed from the children list.
  extractWordFootnotes(doc);

  const body = doc.body;

  // Convert <br>-fused soft line breaks into real paragraph siblings BEFORE
  // list-grouping runs, so list detection sees one parsed paragraph per line.
  splitAllBrParagraphs(body);

  const children = Array.from(body.children);
  let resultHtml = "";
  let group: Parsed[] = [];

  const flush = () => {
    if (group.length > 0) {
      resultHtml += renderGroup(group);
      group = [];
    }
  };

  for (const node of children) {
    if (node.tagName.toLowerCase() === "p") {
      const parsed = parseListyParagraph(node as HTMLParagraphElement);
      if (parsed) {
        group.push(parsed);
        continue;
      }
    }
    flush();
    // Strip remaining word attrs on the paragraph itself.
    if (node instanceof HTMLElement) {
      node.removeAttribute("class");
      node.removeAttribute("style");
      node.removeAttribute("lang");
    }
    resultHtml += node.outerHTML;
  }
  flush();
  return resultHtml;
};

// Split a textblock node that contains hardBreak children into multiple
// sibling nodes of the same type/attrs. Inline marks are preserved. This is
// the bulletproof guard that ensures heading/indent/list toggles only affect
// the visible line the user selected, regardless of how the clipboard HTML
// or plain text was parsed.
const splitTextblockOnHardBreak = (node: PMNode): PMNode[] => {
  if (!node.isTextblock) return [node];
  // Only split paragraphs and headings — leave code blocks etc. alone.
  if (node.type.name !== "paragraph" && node.type.name !== "heading") return [node];

  let hasBreak = false;
  const groups: PMNode[][] = [[]];
  node.content.forEach((child) => {
    if (child.type.name === "hardBreak") {
      hasBreak = true;
      groups.push([]);
    } else {
      groups[groups.length - 1].push(child);
    }
  });
  if (!hasBreak) return [node];

  // Drop trailing empty group (from trailing hardBreak).
  while (groups.length > 1 && groups[groups.length - 1].length === 0) groups.pop();

  return groups.map((children) =>
    node.type.create(node.attrs, Fragment.fromArray(children), node.marks),
  );
};

const splitFragmentHardBreaks = (frag: Fragment): Fragment => {
  const out: PMNode[] = [];
  frag.forEach((child) => {
    if (child.isTextblock) {
      for (const piece of splitTextblockOnHardBreak(child)) out.push(piece);
      return;
    }
    // Recurse into block containers (e.g. blockquote) but NOT into list items,
    // tables, or atom-like nodes where <br> is intentional.
    const name = child.type.name;
    const skip =
      name === "listItem" ||
      name === "table" ||
      name === "tableRow" ||
      name === "tableCell" ||
      name === "tableHeader" ||
      child.isAtom;
    if (!skip && child.content.size > 0) {
      out.push(child.copy(splitFragmentHardBreaks(child.content)));
    } else {
      out.push(child);
    }
  });
  return Fragment.fromArray(out);
};

// Plain-text paste: turn newline-delimited text into real <p> blocks so the
// clipboard text parser does not collapse everything into a single textblock
// joined by hardBreaks.
const transformPlainText = (text: string): string => {
  if (!text) return text;
  // Normalize newlines and collapse 3+ blank lines into a single blank.
  const normalized = text.replace(/\r\n?/g, "\n").replace(/\n{3,}/g, "\n\n");
  const lines = normalized.split("\n");
  return lines
    .map((l) => (l.trim() === "" ? "<p></p>" : `<p>${escapeHtml(l)}</p>`))
    .join("");
};

export const WordPasteSanitizer = Extension.create({
  name: "wordPasteSanitizer",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("wordPasteSanitizer"),
        props: {
          transformPastedHTML(html) {
            try {
              return transform(html);
            } catch {
              return html;
            }
          },
          // (Plain-text paste is also covered by transformPasted below,
          // which splits hardBreaks in the resulting slice.)

          // Final safety net: even after the HTML / text transforms above,
          // some sources leave hardBreaks inside the resulting paragraphs.
          // Split them into real sibling paragraphs at the slice level.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- duplicate prosemirror-model copies make Slice types nominally incompatible
          transformPasted(slice: any) {
            try {
              const next = splitFragmentHardBreaks(slice.content);
              return new Slice(next, slice.openStart, slice.openEnd);
            } catch {
              return slice;
            }
          },
        },
      }),
    ];
  },
});

export const __test = {
  transform,
  parseListyParagraph,
  renderGroup,
  splitTextblockOnHardBreak,
  splitFragmentHardBreaks,
  transformPlainText,
  extractWordFootnotes,
};

